/**
 * True when serialized Fabric canvas JSON contains printable objects.
 * Empty shells (visited Front with no art) and grid guides
 * (`removeFromFinalImage`) must not count — they inflated DTG placement
 * pricing and created blank Front mockups for Back + Left Chest designs.
 */
export function canvasDataHasRealArt(canvasData) {
  if (!canvasData) return false;
  try {
    const parsed =
      typeof canvasData === "string" ? JSON.parse(canvasData) : canvasData;
    const objects = parsed?.objects;
    if (!Array.isArray(objects) || objects.length === 0) return false;
    return objects.some((obj) => obj && !obj.removeFromFinalImage);
  } catch {
    return false;
  }
}

/**
 * True when a placement has real artwork (layer map or non-empty canvas).
 * Prefer this over `Boolean(canvasData)` — empty Front shells are common
 * because the designer defaults to Front and persists canvas JSON on leave.
 */
export function placementHasRealArt(positionData) {
  if (!positionData || typeof positionData !== "object") return false;
  const hasSelectedObjects =
    positionData.selectedObjects &&
    Object.keys(positionData.selectedObjects).length > 0;
  return hasSelectedObjects || canvasDataHasRealArt(positionData.canvasData);
}

/** True when any color/placement in a designer tree has printable art. */
export function designerHasRealArt(placementDetails) {
  if (!placementDetails || typeof placementDetails !== "object") return false;
  return Object.values(placementDetails).some((colorBlock) => {
    if (!colorBlock || typeof colorBlock !== "object") return false;
    return Object.values(colorBlock).some((pd) => placementHasRealArt(pd));
  });
}

const colorBucketHasArt = (tree, color) =>
  Object.values(tree?.[color] || {}).some((node) => placementHasRealArt(node));

/**
 * Art for catalog products is stored under "default" until the seller clicks
 * "Make specific design" for a color. Selecting a shirt color must not fork
 * the design. An empty color bucket must not hide the default art either.
 * A color in `splitColors` was split on purpose and stays on its own bucket
 * even before any art is uploaded or after its art is cleared to be replaced.
 */
export function colorKeyForEditorArt(
  placementDetails,
  backgroundColor,
  splitColors = [],
) {
  const color = backgroundColor || "default";
  if (color === "default") return "default";
  if (
    (splitColors || []).includes(color) ||
    colorBucketHasArt(placementDetails, color)
  ) {
    return color;
  }
  return "default";
}

/**
 * Placement node for the open side, falling back to default when that color has the art.
 * A split color never falls back: it was seeded from default when split, so
 * an empty side there was cleared on purpose.
 */
export function placementNodeForEditor(tree, colorKey, placementId, splitColors = []) {
  const id = String(placementId ?? "");
  if (!id) return null;
  const keyed = tree?.[colorKey]?.[id];
  if (placementHasRealArt(keyed)) return keyed;
  if (colorKey !== "default" && (splitColors || []).includes(colorKey)) {
    return keyed || null;
  }
  const fallback = colorKey === "default" ? null : tree?.default?.[id];
  if (placementHasRealArt(fallback)) return fallback;
  return keyed || fallback || null;
}

/**
 * A side that already has artwork keeps the method saved on that side.
 * The blank's default (often DTG) is only a fallback before art loads.
 */
export function savedDecorationMethod(placement, remembered) {
  const saved = `${placement?.decorationMethod || ""}`;
  if (placementHasRealArt(placement) && saved) return saved;
  return `${remembered || ""}` || saved;
}

/**
 * True when the live placement is only the pre-load stub `{ position }`.
 * Writing that stub over a saved design is what left Edit Design on an empty shirt.
 * A user clear sets selectedObjects, so it is allowed through.
 */
export function incomingWouldWipeSavedArt(previous, incoming) {
  if (!placementHasRealArt(previous) || placementHasRealArt(incoming)) return false;
  if (!incoming || typeof incoming !== "object") return true;
  const hasLayerMap = Object.prototype.hasOwnProperty.call(incoming, "selectedObjects");
  const hasCanvas = Object.prototype.hasOwnProperty.call(incoming, "canvasData");
  return !hasLayerMap && !hasCanvas;
}

/**
 * Bulk create stores the print file on selectedObjects and leaves canvasData
 * null. Fabric only paints canvas JSON, so the shirt stays blank unless this
 * URL is placed like a dropped file.
 */
export function artworkFileFromLayers(selectedObjects) {
  if (!selectedObjects || typeof selectedObjects !== "object") return null;
  for (const obj of Object.values(selectedObjects)) {
    if (!obj || typeof obj !== "object") continue;
    const direct = typeof obj.url === "string" ? obj.url : "";
    const uploaded =
      typeof obj.actualUploadedFile?.url === "string"
        ? obj.actualUploadedFile.url
        : "";
    const url = /^https?:\/\//i.test(direct)
      ? direct
      : /^https?:\/\//i.test(uploaded)
        ? uploaded
        : "";
    if (!url) continue;
    return {
      id: obj.id || obj.actualUploadedFile?.id || url,
      url,
      name: obj.name || obj.actualUploadedFile?.name || "Design",
      type: obj.type || "printing",
    };
  }
  return null;
}

/**
 * Text layers are sometimes saved as a blob: image that dies when the tab closes.
 * Rebuild those as fabric text from the stored words so the side is not blank.
 * Real https artwork and blob: image layers (uploaded neck labels, placed
 * graphics) are left untouched; their file name is not text to print.
 */
// A layer only carries its File while this tab is open; after a reload it
// comes back from storage without one. So a File means every blob: URL the
// canvas has for it (including the previous one, mid-recolor) is still live.
function isHttpsUrl(value) {
  return /^https:\/\//i.test(String(value || ""));
}

/**
 * Point blob: image sources in one placement's canvas JSON at the https URL
 * its layer was uploaded to. Returns the original string when nothing changes.
 */
export function canvasDataWithLayerUrls(canvasData, selectedObjects) {
  if (typeof canvasData !== "string" || !canvasData.includes("blob:")) {
    return canvasData;
  }
  let parsed;
  try {
    parsed = JSON.parse(canvasData);
  } catch {
    return canvasData;
  }
  let changed = false;
  (parsed?.objects || []).forEach((obj) => {
    if (!String(obj?.src || "").startsWith("blob:")) return;
    const url = selectedObjects?.[obj.objectId]?.url;
    if (!isHttpsUrl(url)) return;
    obj.src = url;
    changed = true;
  });
  return changed ? JSON.stringify(parsed) : canvasData;
}

/** canvasDataWithLayerUrls for every color × placement in a design tree. */
export function treeWithLayerUrls(tree) {
  if (!tree || typeof tree !== "object") return tree;
  let changed = false;
  const next = {};
  Object.entries(tree).forEach(([color, byPlacement]) => {
    if (!byPlacement || typeof byPlacement !== "object") {
      next[color] = byPlacement;
      return;
    }
    const placements = {};
    Object.entries(byPlacement).forEach(([pid, place]) => {
      if (!place || typeof place !== "object") {
        placements[pid] = place;
        return;
      }
      const canvasData = canvasDataWithLayerUrls(
        place.canvasData,
        place.selectedObjects,
      );
      if (canvasData !== place.canvasData) {
        changed = true;
        placements[pid] = { ...place, canvasData };
      } else {
        placements[pid] = place;
      }
    });
    next[color] = placements;
  });
  return changed ? next : tree;
}

function layerHoldsLiveBlob(layer) {
  const file = layer?.file;
  return Boolean(file && typeof Blob !== "undefined" && file instanceof Blob);
}

/** Scale text rebuilt by repairBlobCanvasData back to its on-shirt width. */
export function fitRepairedTextObject(obj) {
  const target = Number(obj?.repairTargetWidth);
  const width = Number(obj?.width);
  if (!(target > 0) || !(width > 0)) return false;
  const scale = target / width;
  obj.set({ scaleX: scale, scaleY: scale, repairTargetWidth: null });
  obj.setCoords?.();
  return true;
}

export function repairBlobCanvasData(canvasData, selectedObjects) {
  if (typeof canvasData !== "string" || !canvasData.includes("blob:")) {
    return canvasData;
  }
  let parsed;
  try {
    parsed = JSON.parse(canvasData);
  } catch {
    return canvasData;
  }
  if (!Array.isArray(parsed?.objects)) return canvasData;
  let changed = false;
  const objects = [];
  parsed.objects.forEach((obj) => {
    const src = String(obj?.src || "");
    if (!src.startsWith("blob:")) {
      objects.push(obj);
      return;
    }
    const layer = selectedObjects?.[obj.objectId] || {};
    if (!layer.isText && !layer.font) {
      objects.push(obj);
      return;
    }
    // Text added in this session still holds its File, so the blob: URL is
    // alive. Rebuilding it as live text here replaced every new text layer
    // with an invisible 11px copy. Only repair blobs from a closed tab.
    if (layerHoldsLiveBlob(layer)) {
      objects.push(obj);
      return;
    }
    // Save uploads each text image and stores the https URL on the layer,
    // but the canvas JSON could still hold the dead blob. Point the canvas at
    // the uploaded image: same pixels, same font, same size.
    if (isHttpsUrl(layer.url)) {
      changed = true;
      objects.push({ ...obj, src: layer.url });
      return;
    }
    const text = String(layer.text || layer.name || "").trim();
    changed = true;
    objects.push({
      type: "text",
      version: obj.version,
      originX: obj.originX || "left",
      originY: obj.originY || "top",
      left: obj.left,
      top: obj.top,
      scaleX: obj.scaleX || 1,
      scaleY: obj.scaleY || 1,
      angle: obj.angle || 0,
      fill: layer.color || "#111111",
      fontFamily: layer.font || "Arial",
      fontWeight: layer.fontWeight || "normal",
      fontStyle: layer.fontStyle || "normal",
      fontSize: Number(layer.fontSize) || 48,
      text: text || "Text",
      objectId: obj.objectId,
      // The image's scale was for its own pixel width. Keep the on-shirt
      // width instead; the canvas reload rescales to this after measuring.
      repairTargetWidth:
        Number(obj.width) > 0 ? Number(obj.width) * (Number(obj.scaleX) || 1) : null,
    });
  });
  if (!changed) return canvasData;
  return JSON.stringify({ ...parsed, objects });
}

/**
 * Where a live canvas snapshot belongs when the editor is about to dispose it.
 * The canvas is stamped with the color and placement it was built for. After a
 * swatch click those selected values have already moved on, and writing the
 * snapshot into currentPlacement would paste this shirt's size onto the next one.
 * Returning updateCurrent false keeps the size on the shirt being left.
 */
export function liveCanvasCommitTarget({
  canvasPlacement,
  canvasColor,
  selectedPlacement,
  selectedColor,
} = {}) {
  const hasCanvasPlacement = canvasPlacement != null && canvasPlacement !== "";
  const hasCanvasColor = canvasColor != null && canvasColor !== "";
  const placementKey = hasCanvasPlacement
    ? String(canvasPlacement)
    : selectedPlacement != null
      ? String(selectedPlacement)
      : "";
  const colorKey = hasCanvasColor
    ? String(canvasColor)
    : selectedColor != null && selectedColor !== ""
      ? String(selectedColor)
      : "default";
  const leavingColor =
    hasCanvasColor && String(canvasColor) !== String(selectedColor ?? "");
  const leavingPlacement =
    hasCanvasPlacement &&
    String(canvasPlacement) !== String(selectedPlacement ?? "");
  return {
    placementKey,
    colorKey,
    updateCurrent: !leavingColor && !leavingPlacement,
  };
}

/**
 * Load a text layer's font before Fabric draws it. Live text restored from a
 * saved design otherwise paints in the browser's serif fallback, and a
 * re-save exported that fallback as the print file.
 */
export async function loadFontForCanvasText(fontFamily) {
  const family = String(fontFamily || "").trim();
  if (!family || typeof document === "undefined") return false;
  try {
    const { ensureEnterpriseFontFamily } = await import("./enterpriseFonts");
    await ensureEnterpriseFontFamily(family);
  } catch (_) {
    /* custom fonts are optional */
  }
  try {
    const WebFont = (await import("webfontloader")).default;
    await new Promise((resolve) => {
      WebFont.load({
        google: { families: [family] },
        active: resolve,
        inactive: resolve,
        timeout: 4000,
      });
    });
  } catch (_) {
    /* fall through to the browser check */
  }
  try {
    await document.fonts?.load?.(`48px "${family}"`);
    return Boolean(document.fonts?.check?.(`48px "${family}"`));
  } catch (_) {
    return false;
  }
}

/**
 * True when serialized canvas JSON is an out-of-date echo of the live canvas:
 * the canvas has artwork layers the placement still lists in selectedObjects,
 * and the JSON is missing at least one of them. Reloading it would erase art
 * the editor still owns. Grid guides and id-less objects are ignored.
 */
export function isStaleCanvasSnapshot(canvas, canvasData, selectedObjects) {
  const live = (canvas?.getObjects?.() || []).filter(
    (obj) => obj?.objectId && !obj.removeFromFinalImage,
  );
  if (!live.length || typeof canvasData !== "string") return false;
  const owned = selectedObjects || {};
  if (!live.every((obj) => owned[obj.objectId])) return false;
  let incoming;
  try {
    incoming = new Set(
      (JSON.parse(canvasData)?.objects || [])
        .map((obj) => obj?.objectId)
        .filter(Boolean),
    );
  } catch {
    return false;
  }
  return live.some((obj) => !incoming.has(obj.objectId));
}

/**
 * Bulk created products saved before the server wrote designer art carry
 * their print files only in `merchDrop`. Returns a tree with that art in
 * `selectedObjects` (same shape the server writes now), or the input tree
 * unchanged when it already has art or there is nothing to seed.
 */
export function seedMerchDropArtTree(tree, merchDrop) {
  if (!merchDrop || designerHasRealArt(tree)) return tree;
  const https = (u) =>
    typeof u === "string" && /^https?:\/\//i.test(u) ? u : null;
  const places =
    Array.isArray(merchDrop.placements) && merchDrop.placements.length
      ? merchDrop.placements
      : [{ placementId: merchDrop.placementId || "1", designUrl: merchDrop.designUrl }];
  const base = tree && typeof tree === "object" ? tree : {};
  const block = { ...(base.default || {}) };
  let seeded = false;
  places.forEach((pl) => {
    const pid = String(pl?.placementId || "");
    // Neck labels are per size and come from merchDrop.neckLabel instead.
    if (!pid || pid === "5") return;
    const url = https(pl?.designUrl) || https(merchDrop.designUrl);
    if (!url) return;
    const id = `bulk_${pid}`;
    block[pid] = {
      previewImg: null,
      objectDetails: {},
      selectedImageForCanvas: null,
      position: pid,
      canvasData: null,
      ...(block[pid] || {}),
      selectedObjects: { [id]: { id, url, name: "Design", type: "printing" } },
      ...(pl?.decorationMethod ? { decorationMethod: String(pl.decorationMethod) } : {}),
    };
    seeded = true;
  });
  return seeded ? { ...base, default: block } : tree;
}
